// @givmo/app-charity — auth-screen validation (Zod) + password-rule predicates.
//
// Reuses the same email/name/password rules as the admin app (ported from the webapp `Regexes`), but
// scoped to the charity portal's screens: sign-in (email + password), sign-up (name + email +
// password), and forgot-password. There is NO company-details schema — charity org creation is the
// claim flow (CPA-WEB2), not sign-up.

import { z } from 'zod';

/** Email format (verbatim from the webapp `Regexes.emailValid`). */
export const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

/** "Has both letters and numbers" (verbatim `Regexes.hasLettersAndNumbers`). */
export const HAS_LETTERS_AND_NUMBERS_REGEX = /^(?=.*[A-Za-z])(?=.*\d).+$/;

/** "Has at least one symbol" (verbatim `Regexes.atLeastOneSymbol`). */
export const HAS_SYMBOL_REGEX = /^(?=.*\W).+$/;

export const INVALID_EMAIL_MESSAGE = 'Invalid email format';
export const REQUIRED_NAME_MESSAGE = 'Please enter your name';

// ── Password-rule predicates (the 3 live indicators; mirror the webapp submit gate) ────────────────

export function passwordHasMinLength(password: string): boolean {
  return password.length > 7;
}

export function passwordHasLettersAndNumbers(password: string): boolean {
  return HAS_LETTERS_AND_NUMBERS_REGEX.test(password);
}

export function passwordHasSymbol(password: string): boolean {
  return HAS_SYMBOL_REGEX.test(password);
}

/** All three password rules satisfied. */
export function passwordSatisfiesAllRules(password: string): boolean {
  return (
    passwordHasMinLength(password) &&
    passwordHasLettersAndNumbers(password) &&
    passwordHasSymbol(password)
  );
}

/** Sign-in: email (format) + password (non-empty) + rememberMe. */
export const signInSchema = z.object({
  email: z.string().regex(EMAIL_REGEX, INVALID_EMAIL_MESSAGE),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean(),
});
export type SignInFormValues = z.infer<typeof signInSchema>;

/** Forgot-password: email only (format). */
export const forgotPasswordSchema = z.object({
  email: z.string().regex(EMAIL_REGEX, INVALID_EMAIL_MESSAGE),
});
export type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

/** Sign-up: first/last name (non-empty), email (format), and the 3 password rules. */
export const signUpSchema = z.object({
  firstName: z.string().trim().min(1, REQUIRED_NAME_MESSAGE),
  lastName: z.string().trim().min(1, REQUIRED_NAME_MESSAGE),
  email: z.string().regex(EMAIL_REGEX, INVALID_EMAIL_MESSAGE),
  password: z
    .string()
    .refine(passwordHasMinLength, 'Password must be at least 8 characters')
    .refine(passwordHasLettersAndNumbers, 'Password must contain letters and numbers')
    .refine(passwordHasSymbol, 'Password must contain a symbol'),
});
export type SignUpFormValues = z.infer<typeof signUpSchema>;
